/**
 * The currencies and time zones a person picks from.
 *
 * A short, ordered list beats every ISO code ever minted: the ones this
 * business actually trades in come first, and the rest are there for the day
 * somebody signs a client in Nairobi. Codes are what is stored; the names are
 * only so nobody has to know that MYR is the ringgit.
 */

export type Currency = { code: string; name: string; symbol: string };

/** The Gulf and the currencies this company bills in, then the rest. */
export const CURRENCIES: Currency[] = [
  { code: 'AED', name: 'UAE dirham', symbol: 'AED' },
  { code: 'USD', name: 'US dollar', symbol: '$' },
  { code: 'SAR', name: 'Saudi riyal', symbol: 'SAR' },
  { code: 'QAR', name: 'Qatari riyal', symbol: 'QAR' },
  { code: 'KWD', name: 'Kuwaiti dinar', symbol: 'KWD' },
  { code: 'BHD', name: 'Bahraini dinar', symbol: 'BHD' },
  { code: 'OMR', name: 'Omani rial', symbol: 'OMR' },
  { code: 'PKR', name: 'Pakistani rupee', symbol: '₨' },
  { code: 'INR', name: 'Indian rupee', symbol: '₹' },
  { code: 'GBP', name: 'Pound sterling', symbol: '£' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'CAD', name: 'Canadian dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian dollar', symbol: 'A$' },
  { code: 'SGD', name: 'Singapore dollar', symbol: 'S$' },
  { code: 'MYR', name: 'Malaysian ringgit', symbol: 'RM' },
  { code: 'CNY', name: 'Chinese yuan', symbol: '¥' },
  { code: 'JPY', name: 'Japanese yen', symbol: '¥' },
  { code: 'CHF', name: 'Swiss franc', symbol: 'CHF' },
  { code: 'TRY', name: 'Turkish lira', symbol: '₺' },
  { code: 'EGP', name: 'Egyptian pound', symbol: 'E£' },
  { code: 'JOD', name: 'Jordanian dinar', symbol: 'JOD' },
  { code: 'LKR', name: 'Sri Lankan rupee', symbol: 'Rs' },
  { code: 'BDT', name: 'Bangladeshi taka', symbol: '৳' },
  { code: 'NPR', name: 'Nepalese rupee', symbol: 'Rs' },
  { code: 'ZAR', name: 'South African rand', symbol: 'R' },
  { code: 'KES', name: 'Kenyan shilling', symbol: 'KSh' },
  { code: 'NGN', name: 'Nigerian naira', symbol: '₦' },
  { code: 'RUB', name: 'Russian rouble', symbol: '₽' },
  { code: 'HKD', name: 'Hong Kong dollar', symbol: 'HK$' },
  { code: 'NZD', name: 'New Zealand dollar', symbol: 'NZ$' },
];

/** "AED — UAE dirham", for a select. */
export function currencyLabel(code: string): string {
  const found = CURRENCIES.find((currency) => currency.code === code);
  return found ? `${found.code} — ${found.name}` : code;
}

/**
 * Time zones, the ones this business works across first.
 *
 * A curated list rather than the full IANA database: a dropdown of six
 * hundred entries is not a choice, it is a search problem.
 */
export const TIMEZONES: string[] = [
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Qatar',
  'Asia/Kuwait',
  'Asia/Bahrain',
  'Asia/Muscat',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Colombo',
  'Asia/Kathmandu',
  'Asia/Singapore',
  'Asia/Kuala_Lumpur',
  'Asia/Jakarta',
  'Asia/Manila',
  'Asia/Bangkok',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Tehran',
  'Asia/Baghdad',
  'Asia/Amman',
  'Asia/Beirut',
  'Asia/Jerusalem',
  'Asia/Istanbul',
  'Africa/Cairo',
  'Africa/Nairobi',
  'Africa/Lagos',
  'Africa/Johannesburg',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
  'Australia/Sydney',
  'Australia/Perth',
  'Pacific/Auckland',
  'UTC',
];

/** "Asia/Dubai (GMT+4)" — the offset is what people actually check. */
export function timezoneLabel(zone: string): string {
  try {
    const formatted = new Intl.DateTimeFormat('en', {
      timeZone: zone,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
    return formatted ? `${zone.replace(/_/g, ' ')} (${formatted})` : zone.replace(/_/g, ' ');
  } catch {
    // An unknown zone is still a zone somebody typed; show it as it is.
    return zone;
  }
}
